import { io, Socket } from 'socket.io-client';
import config from 'src/config';
import BillingApi from '@/services/rest/bemob/billing';
import ColumnSettingsApi from '@/services/rest/bemob/column-settings';
import store from '@/store';
import TrafficSourceAPI from '@/services/rest/bemob/traffic-source';
import AffiliateNetworkAPI from '@/services/rest/bemob/affiliate-network';
import FlowApi from '@/services/rest/bemob/flow';
import CampaignApi from '@/services/rest/bemob/campaign';
import TagApi from '@/services/rest/bemob/tag';
import DomainsApi from '@/services/rest/bemob/domains';
import IntegrationsApi from  '@/services/rest/bemob/integrations';
import LandingApi from '@/services/rest/bemob/landing';
import OfferApi from '@/services/rest/bemob/offer';
import {debounce} from 'lodash';

const billingApi: BillingApi = new BillingApi();
const columnSettingsApi: ColumnSettingsApi = new ColumnSettingsApi();
const trafficSourceApi: TrafficSourceAPI = new TrafficSourceAPI();
const affiliateNetworkAPI: AffiliateNetworkAPI = new AffiliateNetworkAPI();
const flowApi: FlowApi = new FlowApi();
const campaignApi: CampaignApi = new CampaignApi();
const tagApi: TagApi = new TagApi();
const domainsApi: DomainsApi = new DomainsApi();
const integrationsApi: IntegrationsApi = new IntegrationsApi();
const landingApi: LandingApi = new LandingApi();
const offerApi: OfferApi = new OfferApi();

let socket: Socket = null;

const connectWebsocket = ()=>{
  socket = io(config.api.baseURL, {
    query: {
      accessToken:localStorage.getItem('auth-token'),
    },
    transports: ['websocket']
  });

  socket.on('http-cache-version', (version: string) => {
    if (process.env.VUE_APP_CONFIG_NAME !== 'production') {
      console.log('[Socket] `http-cache-version` received', version);
    }
    setCacheVersion(version, {broadcast: false});
  });

  socket.on('subscription-updated', (data: { timestamp: Date,userId:string }) => {
    if (process.env.VUE_APP_CONFIG_NAME !== 'production') {
      console.log('[Socket] `subscription-updated` received', data);
    }
    billingApi.getSubscriptions()

    if(!store.state.user.isMember){
      billingApi.getCustomer()
      billingApi.getAddons()
    }
    
    columnSettingsApi.getColumnSettings().then(res=>store.dispatch('setSavedColumns', res));
  });

  socket.on('campaigns-updated', (data: { timestamp: Date, userId:string, eventData:{tagsChanged: boolean} }) => {
    campaignApi.populateStoreCache()
    if(data?.eventData?.tagsChanged){
      store.commit('cacheStartUpdating', {cacheProperty:'tags', cachePropertyLevel2:'campaigns'});
      tagApi.populateStoreCache({type:'campaigns'})
    }
  });

  socket.on('offers-updated', (data: { timestamp: Date, userId:string, eventData:{tagsChanged: boolean} }) => {
    store.commit('cacheStartUpdating', {cacheProperty:'offers'});
    offerApi.populateStoreCache();
    if(data?.eventData?.tagsChanged){
      store.commit('cacheStartUpdating', {cacheProperty:'tags', cachePropertyLevel2:'offers'});
      tagApi.populateStoreCache({type:'offers'})
    }
  });

  socket.on('landings-updated', (data: { timestamp: Date, userId:string, eventData:{tagsChanged: boolean} }) => {
    store.commit('cacheStartUpdating', {cacheProperty:'landings'});
    landingApi.populateStoreCache()
    if(data?.eventData?.tagsChanged){
      store.commit('cacheStartUpdating', {cacheProperty:'tags', cachePropertyLevel2:'landings'});
      tagApi.populateStoreCache({type:'landings'})
    }
  });

  socket.on('flows-updated', (data: { timestamp: Date, userId:string, eventData:{tagsChanged: boolean} }) => {
    store.commit('cacheStartUpdating', {cacheProperty:'flows'});
    flowApi.populateStoreCache()
    if(data?.eventData?.tagsChanged){
      store.commit('cacheStartUpdating', {cacheProperty:'tags', cachePropertyLevel2:'flows'});
      tagApi.populateStoreCache({type:'flows'})
    }
  });

  socket.on('traffic-sources-updated', (data: { timestamp: Date, userId:string, eventData:{tagsChanged: boolean} }) => {
    store.commit('cacheStartUpdating', {cacheProperty:'trafficSources'});
    trafficSourceApi.populateStoreCache()
    if(data?.eventData?.tagsChanged){
      store.commit('cacheStartUpdating', {cacheProperty:'tags', cachePropertyLevel2:'traffic-sources'});
      tagApi.populateStoreCache({type:'traffic-sources'})
    }
  });

  socket.on('affiliate-networks-updated', (data: { timestamp: Date, userId:string, eventData:{tagsChanged: boolean} }) => {
    store.commit('cacheStartUpdating', {cacheProperty:'affiliateNetworks'});
    affiliateNetworkAPI.populateStoreCache()
    if(data?.eventData?.tagsChanged){
      store.commit('cacheStartUpdating', {cacheProperty:'tags', cachePropertyLevel2:'affiliate-networks'});
      tagApi.populateStoreCache({type:'affiliate-networks'})
    }
  });

  const debouncedTrackingDomainsPopulateStoreCache = debounce(()=>{
    store.commit('cacheStartUpdating', {cacheProperty:'trackingDomains'});
    domainsApi.trackingDomainsPopulateStoreCache()
  }, 500) 
  socket.on('tracking-domains-updated', (data: { timestamp: Date,userId:string }) => debouncedTrackingDomainsPopulateStoreCache());

  socket.on('redirect-domains-updated', (data: { timestamp: Date,userId:string }) => {
    store.commit('cacheStartUpdating', {cacheProperty:'redirectDomains'});
    domainsApi.redirectDomainsPopulateStoreCache()
  });
  
  socket.on('integrations-updated', (data: { timestamp: Date,userId:string }) => {
    store.commit('cacheStartUpdating', {cacheProperty:'integrations'});
    integrationsApi.populateStoreCache()
  });
}

export const setupWebsocket = ()=>{
  if(!socket){
    connectWebsocket()
  }
}

export const reConnectWebsocket = ()=>{
  if(socket?.connected){
    socket.disconnect();
  }
  connectWebsocket()
}

export const disconnectWebsocket = ()=>{
  if(socket?.connected){
    socket.disconnect();
  }
  socket = null;
}

export const setCacheVersion = (version: string, {broadcast} = {broadcast: true}) =>{
  localStorage.setItem('http-cache-version', version);
  if (broadcast && socket && socket.connected) {
    socket.emit('http-cache-version', version);
  }
  return version;
}

export const forceCleanCache = () =>{
  setCacheVersion(`${Date.now()}`);
}